import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Avatar } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { IMerchant } from "@/interfaces/merchant.interfaces";
import countries from "@/constants/array/country_city.json";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import { useForm, useFieldArray } from "react-hook-form";
import * as Yup from "yup";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormField } from "@/components/ui/form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Trash, Loader2 } from "lucide-react";
import { BUSINESS_TYPE } from "@/constants/merchant.constant";
import { toast } from "sonner";

// Helper function to normalize business type
const normalizeBusinessType = (businessType: string | undefined): string => {
  if (!businessType) return BUSINESS_TYPE.OFFLINE;

  const normalized = businessType.toUpperCase();
  return normalized === BUSINESS_TYPE.ONLINE
    ? BUSINESS_TYPE.ONLINE
    : BUSINESS_TYPE.OFFLINE;
};

interface EditMerchantSheetProps {
  merchant: IMerchant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (merchant: any) => void;
}

// Define CURRENCY_OPTIONS directly here
const CURRENCY_OPTIONS = [
  { id: 1, label: "United State Dollar", value: "USD" },
  { id: 2, label: "EURO", value: "EUR" },
  { id: 3, label: "British Pound", value: "GBP" },
  { id: 4, label: "Indian Rupee", value: "INR" },
  { id: 5, label: "United Arab Emirates Dirham", value: "AED" },
  { id: 6, label: "Ethiopian Birr", value: "ETB" },
];

// Add Yup schema for the entire form
const MerchantSchema = Yup.object().shape({
  merchantName: Yup.string().required("Merchant Name is required"),
  merchantCode: Yup.string().required("Merchant code is required."),
  businessLocation: Yup.string(),
  businessType: Yup.string().required("Business type is required."),
  businessDescription: Yup.string().required(
    "Merchant description is required"
  ),
  websiteUrl: Yup.string()
    .required("Website URL is required")
    .url("Website URL must be a valid URL format"),
  offerDomain: Yup.string().when("businessType", {
    is: BUSINESS_TYPE.OFFLINE,
    then: (schema) =>
      schema.required("Offer Domain is required for offline merchants"),
    otherwise: (schema) => schema.optional(),
  }),
  country: Yup.string(),
  city: Yup.string(),
  taxId: Yup.string(),
  currency: Yup.string().required("Payment currency is required"),
  refundPeriodInDays: Yup.string().required("Refund Period is required"),

  categoryVOList: Yup.array()
    .of(
      Yup.object().shape({
        categoryCode: Yup.string(),
        categoryName: Yup.string().required("Category name is required"),
        cashbackPercentage: Yup.number()
          .typeError("Cashback percentage should be a number")
          .required("Cashback percentage is required")
          .positive("Cashback percentage must be greater than 0")
          .min(0.01, "Cashback percentage must be greater than 0")
          .max(100, "Cashback percentage cannot exceed 100%"),
      })
    )
    .min(1, "At least one category is required"),
});

export const EditMerchantSheet: React.FC<EditMerchantSheetProps> = ({
  merchant,
  open,
  onOpenChange,
  onUpdate,
}) => {
  const [businessType, setBusinessType] = useState(
    normalizeBusinessType(merchant?.businessType)
  );
  const [selectedCountry, setSelectedCountry] = useState<string>(
    merchant?.country || ""
  );

  // Set up react-hook-form
  const form = useForm({
    resolver: yupResolver(MerchantSchema),
    mode: "onSubmit",
    defaultValues: {
      merchantName: merchant?.merchantName || "",
      merchantCode: merchant?.merchantCode || "",
      businessLocation: merchant?.businessLocation || "",
      businessType: normalizeBusinessType(merchant?.businessType),
      businessDescription: merchant?.businessDescription || "",
      websiteUrl: merchant?.websiteUrl || "",
      offerDomain: (merchant as any).offerDomain || "",
      country: merchant?.country || "",
      city: merchant?.city || "",
      taxId: (merchant as any).taxId || "",
      currency: merchant?.currencyVO?.id
        ? merchant.currencyVO.id.toString()
        : "",
      refundPeriodInDays: merchant?.refundPeriodInDays?.toString() || "",

      categoryVOList: merchant?.categoryVOList?.length
        ? merchant.categoryVOList.map((cat) => ({
            ...cat,
            cashbackPercentage: cat.cashbackPercentage ?? 0.01,
          }))
        : [{ categoryCode: "", categoryName: "", cashbackPercentage: 0.01 }],
    },
  });

  // Reset form when merchant data changes
  useEffect(() => {
    if (merchant) {
      setBusinessType(normalizeBusinessType(merchant.businessType));
      setSelectedCountry(merchant.country || "");
      setLogo(merchant.merchantImageUri); // Update logo state when merchant changes
      form.reset({
        merchantName: merchant.merchantName || "",
        merchantCode: merchant.merchantCode || "",
        businessLocation: merchant.businessLocation || "",
        businessType: normalizeBusinessType(merchant.businessType),
        businessDescription: merchant.businessDescription || "",
        websiteUrl: merchant.websiteUrl || "",
        offerDomain: (merchant as any).offerDomain || "",
        country: merchant.country || "",
        city: merchant.city || "",
        taxId: (merchant as any).taxId || "",
        currency: merchant.currencyVO?.id
          ? merchant.currencyVO.id.toString()
          : "",
        refundPeriodInDays: merchant.refundPeriodInDays?.toString() || "",
        categoryVOList: merchant.categoryVOList?.length
          ? merchant.categoryVOList.map((cat) => ({
              ...cat,
              cashbackPercentage: cat.cashbackPercentage ?? 0.01,
            }))
          : [{ categoryCode: "", categoryName: "", cashbackPercentage: 0.01 }],
      });
    }
  }, [merchant, form]);

  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory,
  } = useFieldArray({
    control: form.control,
    name: "categoryVOList",
  });

  const [logo, setLogo] = useState<string | undefined>(
    merchant?.merchantImageUri
  );
  const [showLogoError, setShowLogoError] = useState(false);
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          setLogo(event.target.result as string);
          setShowLogoError(false); // Clear error when logo is uploaded
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const countryList = countries as any[];

  const [isLoading, setIsLoading] = useState(false);

  // Debug: Log business type values
  useEffect(() => {
    console.log(
      "EditMerchantSheet - Original businessType:",
      merchant?.businessType
    );
    console.log("EditMerchantSheet - Normalized businessType:", businessType);
    console.log(
      "EditMerchantSheet - Is offline?",
      businessType === BUSINESS_TYPE.OFFLINE
    );
  }, [merchant?.businessType, businessType]);

  // Clear offerDomain validation when switching to online (since field becomes optional)
  useEffect(() => {
    if (businessType === BUSINESS_TYPE.ONLINE) {
      form.clearErrors("offerDomain");
    }
  }, [businessType, form]);

  const handleSubmit = form.handleSubmit(async (data) => {
    // Trigger validation for all fields before submission
    const isValid = await form.trigger();
    if (!isValid) {
      return; // Don't submit if validation fails
    }

    // Additional validation for merchant logo
    if (!logo) {
      setShowLogoError(true);
      toast.error("Merchant logo is required");
      return;
    }

    setIsLoading(true);
    try {
      await onUpdate({
        ...data,
        logo: logo, // Pass the logo state to the update function
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating merchant:", error);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Merchant</SheetTitle>
          <SheetDescription>
            Update merchant information and settings
          </SheetDescription>
        </SheetHeader>

        <div className="py-6">
          <Tabs defaultValue="general">
            <TabsList className="mb-6 w-full">
              <TabsTrigger value="general" className="flex-1">
                General Info
              </TabsTrigger>
              <TabsTrigger value="business" className="flex-1">
                Business & Financial Info
              </TabsTrigger>
              <TabsTrigger value="categories" className="flex-1">
                Categories
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6">
              <div className="space-y-2">
                <Label className="font-semibold text-gray-700 text-base">
                  Merchant Logo*
                </Label>
                <div className="flex items-center gap-4">
                  <Avatar
                    className={`h-16 w-16 rounded-md border ${
                      showLogoError && !logo ? "border-red-500" : ""
                    }`}
                  >
                    <img
                      src={logo || merchant?.merchantImageUri}
                      alt={form.watch("merchantName")}
                      className="object-cover rounded-md"
                    />
                  </Avatar>
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      className="text-sm"
                      onChange={handleLogoChange}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Recommended: 400x400px, max 2MB
                    </p>
                    {showLogoError && !logo && (
                      <p className="text-sm text-red-600 mt-1">
                        Merchant logo is required
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="merchantName"
                  className="font-semibold text-gray-700 text-base"
                >
                  Merchant Name*
                </Label>
                <FormField
                  control={form.control}
                  name="merchantName"
                  render={({ field }) => <Input {...field} />}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="merchantCode"
                  className="font-semibold text-gray-700 text-base"
                >
                  Merchant Code*
                </Label>
                <FormField
                  control={form.control}
                  name="merchantCode"
                  render={({ field }) => <Input {...field} disabled />}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-gray-700 text-base">
                  Merchant Type
                </Label>
                <div className="flex items-center gap-3">
                  <Switch
                    id="merchant-type"
                    checked={businessType === BUSINESS_TYPE.ONLINE}
                    onCheckedChange={(checked) => {
                      const newType = checked
                        ? BUSINESS_TYPE.ONLINE
                        : BUSINESS_TYPE.OFFLINE;
                      setBusinessType(newType);
                      form.setValue("businessType", newType);
                    }}
                  />
                  <span className="text-sm text-gray-600">
                    {businessType === BUSINESS_TYPE.ONLINE
                      ? "Online"
                      : "Offline"}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="businessDescription"
                  className="font-semibold text-gray-700 text-base"
                >
                  Description*
                </Label>
                <FormField
                  control={form.control}
                  name="businessDescription"
                  render={({ field, fieldState }) => (
                    <div className="space-y-1">
                      <Textarea
                        {...field}
                        rows={3}
                        className={fieldState.error ? "border-red-500" : ""}
                      />
                      {fieldState.error && (
                        <p className="text-sm text-red-600">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="websiteUrl"
                  className="font-semibold text-gray-700 text-base"
                >
                  Website URL*
                </Label>
                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field, fieldState }) => (
                    <div className="space-y-1">
                      <Input
                        {...field}
                        className={fieldState.error ? "border-red-500" : ""}
                      />
                      {fieldState.error && (
                        <p className="text-sm text-red-600">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              {businessType !== BUSINESS_TYPE.ONLINE && (
                <div className="space-y-2">
                  <Label
                    htmlFor="offerDomain"
                    className="font-semibold text-gray-700 text-base"
                  >
                    Offer Domain*
                  </Label>
                  <FormField
                    control={form.control}
                    name="offerDomain"
                    render={({ field, fieldState }) => (
                      <div className="space-y-1">
                        <Input
                          {...field}
                          placeholder="https://offers.example.com"
                          className={fieldState.error ? "border-red-500" : ""}
                        />
                        {fieldState.error && (
                          <p className="text-sm text-red-600">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                  <p className="text-xs text-gray-500">
                    The domain where offers will be hosted for this offline
                    merchant
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="business" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="country"
                    className="font-semibold text-gray-700 text-base"
                  >
                    Country
                  </Label>
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <Select
                        onValueChange={(value) => {
                          setSelectedCountry(value);
                          field.onChange(value);
                          // Clear city when country changes
                          form.setValue("city", "");
                        }}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Country" />
                        </SelectTrigger>
                        <SelectContent onWheel={(e) => e.stopPropagation()}>
                          <div className="max-h-[200px] overflow-y-auto">
                            {countryList.map((country) => (
                              <SelectItem
                                key={country?.name}
                                value={country?.name}
                              >
                                {country?.name}
                              </SelectItem>
                            ))}
                          </div>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="city"
                    className="block text-sm font-semibold text-gray-700 text-base"
                  >
                    City
                  </label>
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <VirtualizedCitySelect
                        cities={
                          countryList.find((c) => c.name === selectedCountry)
                            ?.cities || []
                        }
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select City"
                        disabled={
                          !selectedCountry ||
                          !countryList.find((c) => c.name === selectedCountry)
                            ?.cities?.length
                        }
                      />
                    )}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="businessLocation"
                  className="font-semibold text-gray-700 text-base"
                >
                  Business Location
                </Label>
                <FormField
                  control={form.control}
                  name="businessLocation"
                  render={({ field }) => <Input {...field} />}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="taxId"
                  className="font-semibold text-gray-700 text-base"
                >
                  VAT Number
                </Label>
                <FormField
                  control={form.control}
                  name="taxId"
                  render={({ field }) => <Input {...field} />}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="currency"
                  className="font-semibold text-gray-700 text-base"
                >
                  Payment Currency*
                </Label>
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent onWheel={(e) => e.stopPropagation()}>
                        <div className="max-h-[200px] overflow-y-auto">
                          {CURRENCY_OPTIONS.map((currency) => (
                            <SelectItem
                              key={currency.id}
                              value={currency.id.toString()}
                            >
                              {currency.value} - {currency.label}
                            </SelectItem>
                          ))}
                        </div>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="refundPeriodInDays"
                  className="font-semibold text-gray-700 text-base"
                >
                  Refund Period (days)*
                </Label>
                <FormField
                  control={form.control}
                  name="refundPeriodInDays"
                  render={({ field, fieldState }) => (
                    <div className="space-y-1">
                      <Input
                        {...field}
                        type="number"
                        min="1"
                        className={fieldState.error ? "border-red-500" : ""}
                      />
                      {fieldState.error && (
                        <p className="text-sm text-red-600">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </TabsContent>

            <TabsContent value="categories" className="space-y-6">
              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Categories
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {categoryFields.map((field, index) => (
                    <div key={field.id} className="flex items-end gap-4">
                      <FormField
                        control={form.control}
                        name={`categoryVOList.${index}.categoryName`}
                        render={({ field }) => (
                          <div className="flex-1">
                            <Label className="font-semibold text-gray-700 text-base">
                              Category Name
                            </Label>
                            <Input placeholder="Category Name" {...field} />
                          </div>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`categoryVOList.${index}.cashbackPercentage`}
                        render={({ field, fieldState }) => (
                          <div className="flex-1">
                            <Label className="font-semibold text-gray-700 text-base">
                              Cashback %*
                            </Label>
                            <Input
                              type="number"
                              placeholder="0.01"
                              min="0.01"
                              max="100"
                              step="0.01"
                              onWheel={(e) => e.currentTarget.blur()}
                              onKeyDown={(e) => {
                                // Prevent negative sign, 'e', 'E', '+'
                                if (["-", "e", "E", "+"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers between 0.01-100
                                if (
                                  value === "" ||
                                  (parseFloat(value) >= 0.01 &&
                                    parseFloat(value) <= 100)
                                ) {
                                  field.onChange(value);
                                }
                              }}
                              className={
                                fieldState.error ? "border-red-500" : ""
                              }
                              {...field}
                            />
                            {fieldState.error && (
                              <p className="text-sm text-red-600 mt-1">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mb-1 text-red-600 hover:bg-red-100"
                        onClick={() => removeCategory(index)}
                        disabled={categoryFields.length === 1}
                        aria-label="Delete Category"
                      >
                        <Trash className="h-5 w-5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      appendCategory({
                        categoryCode: "",
                        categoryName: "",
                        cashbackPercentage: 0.01,
                      })
                    }
                  >
                    Add Category
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <SheetFooter>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
